import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CustomJoditEditor from '../components/JoditEditor';
import { contentAPI, GeneratedContent, Category } from '../services/contentAPI';
import { imageUploadService } from '../services/imageUpload';

// Interface for form data
interface EditFormData {
  topic: string;
  category: string;
  keywords: string;
  author_name: string;
  author_email: string;
  meta_description: string;
}

const BlogEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // State for loading existing content
  const [loadingContent, setLoadingContent] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<EditFormData>({
    topic: '',
    category: '',
    keywords: '',
    author_name: '',
    author_email: '',
    meta_description: ''
  });
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [editorContent, setEditorContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing content and categories on component mount
  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoadingContent(true);
      setLoadError(null);
      
      // Load categories
      const categoriesData = await contentAPI.getCategories();
      setCategories(categoriesData);
      
      if (!id) {
        setLoadError('잘못된 블로그 ID입니다.');
        return;
      }
      
      // Load existing content
      const contentData = await contentAPI.getContent(parseInt(id));
      
      // Populate form with existing data
      setFormData({
        topic: contentData.title,
        category: contentData.category?.name || '',
        keywords: contentData.keywords?.join(', ') || '',
        author_name: contentData.author_name || '',
        author_email: contentData.author_email || '',
        meta_description: contentData.meta_description || ''
      });
      
      setEditorContent(contentData.content);
      
    } catch (err) {
      console.error('데이터 로딩 실패:', err);
      setLoadError('블로그 글을 불러오는데 실패했습니다.');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleInputChange = (field: keyof EditFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };




  const handleSave = async () => {
    if (!formData.topic.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    if (!editorContent.trim()) {
      setError('내용을 입력해주세요.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const selectedCategory = categories.find(cat => cat.name === formData.category);
      if (!selectedCategory) {
        throw new Error('선택된 카테고리를 찾을 수 없습니다.');
      }

      // Process HTML images before saving
      const processedContent = await imageUploadService.processHtmlImages(
        editorContent, 
        'blog_' + formData.topic.trim().substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_')
      );

      const updateData = {
        title: formData.topic.trim(),
        content: processedContent,
        category_id: selectedCategory.id,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(k => k) : [],
        author_name: formData.author_name || undefined,
        author_email: formData.author_email || undefined,
        meta_description: formData.meta_description || undefined
      };

      await contentAPI.updateContent(parseInt(id!), updateData);
      alert('글이 수정되었습니다!');
      navigate('/blog/manage');
      
    } catch (err) {
      console.error('저장 실패:', err);
      setError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };


  // Loading state
  if (loadingContent) {
    return (
      <Container>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          fontSize: '1.2rem',
          color: '#666'
        }}>
          블로그 글을 불러오는 중...
        </div>
      </Container>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Container>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          gap: '1rem'
        }}>
          <h3 style={{ color: '#dc2626', margin: 0 }}>오류 발생</h3>
          <p style={{ color: '#666', margin: 0 }}>{loadError}</p>
          <button 
            onClick={() => navigate('/blog/manage')}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            목록으로 돌아가기
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <FloatingNav>
        <NavContent>
          <NavTitle>블로그 수정</NavTitle>
          <NavActions>
            <NavButton 
              variant="secondary"
              onClick={() => navigate('/blog/manage')}
            >
              목록으로
            </NavButton>
            <NavButton 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? '저장 중...' : '저장하기'}
            </NavButton>
          </NavActions>
        </NavContent>
      </FloatingNav>

      <MainLayout>
        <Sidebar>
          <SidebarSection>
            <SectionTitle>기본 정보</SectionTitle>
            
            <FormGroup>
              <Label>제목 *</Label>
              <Input
                type="text"
                value={formData.topic}
                onChange={handleInputChange('topic')}
                placeholder="블로그 글 제목을 입력하세요"
              />
            </FormGroup>

            <FormGroup>
              <Label>카테고리</Label>
              <Input
                type="text"
                value={formData.category}
                readOnly
                style={{ backgroundColor: '#f9fafb', cursor: 'not-allowed' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>키워드</Label>
              <Input
                type="text"
                value={formData.keywords}
                onChange={handleInputChange('keywords')}
                placeholder="키워드를 쉼표로 구분해서 입력하세요"
              />
              <HelperText>예: 음식물쓰레기, 친환경, 담비</HelperText>
            </FormGroup>
          </SidebarSection>

          <SidebarSection>
            <SectionTitle>저자 정보</SectionTitle>
            
            <FormGroup>
              <Label>저자명</Label>
              <Input
                type="text"
                value={formData.author_name}
                onChange={handleInputChange('author_name')}
                placeholder="저자명을 입력하세요"
              />
            </FormGroup>

            <FormGroup>
              <Label>저자 이메일</Label>
              <Input
                type="email"
                value={formData.author_email}
                onChange={handleInputChange('author_email')}
                placeholder="author@example.com"
              />
            </FormGroup>

            <FormGroup>
              <Label>메타 설명</Label>
              <TextArea
                value={formData.meta_description}
                onChange={handleInputChange('meta_description')}
                placeholder="검색 결과에 표시될 글 요약을 입력하세요"
                rows={3}
              />
            </FormGroup>
          </SidebarSection>

        </Sidebar>

        <EditorSection>
          <EditorHeader>
            <EditorTitle>콘텐츠 작성</EditorTitle>
            <EditorHelp>
              에디터에서 텍스트 서식을 지정하고, 이미지를 삽입할 수 있습니다.
            </EditorHelp>
          </EditorHeader>

          <EditorWrapper>
            <CustomJoditEditor
              content={editorContent}
              onContentChange={setEditorContent}
              placeholder="여기에 블로그 글 내용을 작성하세요..."
            />
          </EditorWrapper>
        </EditorSection>

      </MainLayout>

      {error && (
        <ErrorAlert>
          <strong>오류:</strong> {error}
          <button onClick={() => setError(null)}>×</button>
        </ErrorAlert>
      )}
    </Container>
  );
};

// Styled Components (BlogWrite와 동일한 스타일 사용)
const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  position: relative;
`;

const FloatingNav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e5e7eb;
  z-index: 1000;
  padding: 1rem 2rem;
`;

const NavContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
`;

const NavActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const NavButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'secondary' ? `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  ` : `
    background: #667eea;
    color: white;
    
    &:hover:not(:disabled) {
      background: #5a67d8;
    }
    
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `}
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 7rem 2rem 2rem;
  min-height: 100vh;

  @media (max-width: 1200px) {
    grid-template-columns: 260px 1fr;
    gap: 1.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 7rem 1rem 2rem;
    gap: 2rem;
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    order: -1;
  }
`;

const SidebarSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 1.5rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;


const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const HelperText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0.5rem 0 0 0;
`;











const EditorSection = styled.section`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 600px;
`;

const EditorHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fafbfc;
`;

const EditorTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
`;

const EditorHelp = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
`;








const EditorWrapper = styled.div`
  flex: 1;
  padding: 1rem;
  
  .jodit-container {
    border: none !important;
  }
  
  .jodit-wysiwyg {
    overflow-y: auto !important;
  }
`;

const ErrorAlert = styled.div`
  position: fixed;
  top: 6rem;
  right: 2rem;
  background: #fee2e2;
  color: #dc2626;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 400px;
  z-index: 1001;
  
  button {
    background: none;
    border: none;
    color: inherit;
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0;
    margin-left: auto;
  }
`;

export default BlogEdit;